import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';

export type SampleStatus = 'pending' | 'completed' | 'noAction';

interface SampleStatusButtonsProps {
  sampleId: string;
  status: SampleStatus;
  disabled?: boolean;
  onMarkCompleted: (id: string) => void;
  onMarkNoAction: (id: string) => void;
  onUnmark: (id: string, fromStatus: 'completed' | 'noAction') => void;
}

const SampleStatusButtons: React.FC<SampleStatusButtonsProps> = ({
  sampleId,
  status,
  disabled = false,
  onMarkCompleted,
  onMarkNoAction,
  onUnmark,
}) => {
  const handleCompletedClick = () => {
    if (status === 'completed') {
      onUnmark(sampleId, 'completed');
    } else {
      onMarkCompleted(sampleId);
    }
  };

  const handleNoActionClick = () => {
    if (status === 'noAction') {
      onUnmark(sampleId, 'noAction');
    } else {
      onMarkNoAction(sampleId);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title={status === 'completed' ? 'Unmark completed' : 'Mark as completed'}>
        <IconButton
          size="small"
          onClick={handleCompletedClick}
          disabled={disabled}
          sx={{
            color: status === 'completed' ? '#4caf50' : 'action.active',
            '&:hover': {
              bgcolor: status === 'completed' ? 'rgba(76, 175, 80, 0.1)' : 'action.hover',
            },
          }}
        >
          {status === 'completed' ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
        </IconButton>
      </Tooltip>
      <Tooltip title={status === 'noAction' ? 'Unmark no action' : 'Mark as no action'}>
        <IconButton
          size="small"
          onClick={handleNoActionClick}
          disabled={disabled}
          sx={{
            color: status === 'noAction' ? '#ff9800' : 'action.active',
            '&:hover': {
              bgcolor: status === 'noAction' ? 'rgba(255, 152, 0, 0.1)' : 'action.hover',
            },
          }}
        >
          {status === 'noAction' ? <FlagIcon /> : <OutlinedFlagIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default SampleStatusButtons;
